import React, { useState, useEffect } from 'react'
import { XMarkIcon, UserIcon, MagnifyingGlassIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { api } from '../services/authService'
import CreditScoreWidget from './clients/CreditScoreWidget'
import CreditHistoryWidget from './clients/CreditHistoryWidget'
import ClientDocumentsTab from './clients/ClientDocumentsTab'
import { validateDocument, getValidationClass, getValidationMessage } from '../utils/validators'

const ClientForm = ({ client, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('dados-gerais') // 'dados-gerais', 'procurador', or 'socios'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    document: '',
    billing_emails: [''],
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    legal_representative: null,
    partners: [{
      name: '',
      document: '',
      rg: '',
      nationality: 'Brasileira',
      email: '',
      participation: '',
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      cnh_file: null,
      cnh_file_path: '',
      cnh_data: null
    }]
  })

  const [loading, setLoading] = useState(false)
  const [loadingCNPJ, setLoadingCNPJ] = useState(false)
  const [loadingCEP, setLoadingCEP] = useState(false)
  const [loadingPartnerCEP, setLoadingPartnerCEP] = useState({})
  const [loadingPartnerOCR, setLoadingPartnerOCR] = useState({})
  const [documentTouched, setDocumentTouched] = useState(false)
  const [partnerDocumentsTouched, setPartnerDocumentsTouched] = useState({})

  const isEditing = !!client

  useEffect(() => {
    if (client) {
      console.log('📋 Carregando cliente para edição:', client)
      console.log('📋 Partners do cliente:', client.partners)

      setFormData({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        document: client.document || '',
        billing_emails: client.billing_emails && client.billing_emails.length > 0 ? client.billing_emails : [''],
        cep: client.address?.cep || '',
        street: client.address?.street || '',
        number: client.address?.number || '',
        complement: client.address?.complement || '',
        neighborhood: client.address?.neighborhood || '',
        city: client.address?.city || '',
        state: client.address?.state || '',
        legal_representative: client.legal_representative || null,
        partners: client.partners && client.partners.length > 0 ? client.partners.map((p, idx) => {
          console.log(`📋 Partner ${idx}:`, p)
          return {
            ...p,
            cep: p.cep || '',
            street: p.street || '',
            number: p.number || '',
            complement: p.complement || '',
            neighborhood: p.neighborhood || '',
            city: p.city || '',
            state: p.state || '',
            nationality: p.nationality || 'Brasileiro',
            cnh_file: null,
            cnh_file_path: p.cnh_file_path || '',
            cnh_data: p.cnh_data || null
          }
        }) : [{
          name: '',
          document: '',
          rg: '',
          nationality: 'Brasileiro',
          email: '',
          participation: '',
          cep: '',
          street: '',
          number: '',
          complement: '',
          neighborhood: '',
          city: '',
          state: '',
          cnh_file: null,
          cnh_file_path: '',
          cnh_data: null
        }]
      })
    }
  }, [client])

  const handleInputChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '')
    
    if (value.length <= 10) {
      value = value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
    } else {
      value = value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    }
    
    setFormData(prev => ({ ...prev, phone: value }))
  }

  const handleDocumentChange = async (e) => {
    let value = e.target.value.replace(/\D/g, '')
    
    let formattedValue = value
    if (value.length <= 11) {
      formattedValue = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    } else {
      formattedValue = value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    }
    
    setFormData(prev => ({ ...prev, document: formattedValue }))

    if (value.length === 14) {
      await searchCNPJ(value)
    }
  }

  const handleCEPChange = async (e) => {
    let value = e.target.value.replace(/\D/g, '')
    let formattedValue = value.replace(/(\d{5})(\d{3})/, '$1-$2')
    
    setFormData(prev => ({ ...prev, cep: formattedValue }))

    if (value.length === 8) {
      await searchCEP(value)
    }
  }

  const searchCNPJ = async (cnpj) => {
    setLoadingCNPJ(true)
    try {
      const response = await api.get(`/api/receitaws/cnpj/${cnpj}`)
      const result = response.data

      if (result.success && result.data) {
        const data = result.data

        setFormData(prev => ({
          ...prev,
          name: data.razao_social || data.nome_fantasia || prev.name,
          email: data.email || prev.email,
          phone: data.telefone || prev.phone,
          cep: data.cep?.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2') || prev.cep,
          street: data.logradouro || prev.street,
          number: data.numero || prev.number,
          complement: data.complemento || prev.complement,
          neighborhood: data.bairro || prev.neighborhood,
          city: data.municipio || prev.city,
          state: data.uf || prev.state
        }))

        toast.success(`✅ CNPJ encontrado: ${data.razao_social}`, { duration: 4000 })
      } else {
        toast.error('CNPJ não encontrado na Receita Federal')
      }
    } catch (error) {
      console.error('Erro ao buscar CNPJ:', error)

      if (error.response?.status === 429) {
        toast.error('⏱️ Limite de consultas excedido. Aguarde 1 minuto.', { duration: 5000 })
      } else if (error.response?.status === 404) {
        toast.error('CNPJ não encontrado ou inválido')
      } else {
        toast.error('Erro ao consultar CNPJ na Receita Federal')
      }
    } finally {
      setLoadingCNPJ(false)
    }
  }

  const searchCEP = async (cep) => {
    setLoadingCEP(true)
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
      
      if (response.ok) {
        const data = await response.json()
        
        if (data.erro) {
          toast.error('CEP não encontrado')
        } else {
          setFormData(prev => ({
            ...prev,
            street: data.logradouro || prev.street,
            neighborhood: data.bairro || prev.neighborhood,
            city: data.localidade || prev.city,
            state: data.uf || prev.state,
            complement: data.complemento || prev.complement
          }))
          
          toast.success('Endereço preenchido automaticamente!')
        }
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error)
      toast.error('Erro ao consultar CEP')
    } finally {
      setLoadingCEP(false)
    }
  }

  const handlePartnerCEPChange = async (index, e) => {
    let value = e.target.value.replace(/\D/g, '')
    let formattedValue = value.replace(/(\d{5})(\d{3})/, '$1-$2')

    handlePartnerChange(index, 'cep', formattedValue)

    if (value.length === 8) {
      await searchPartnerCEP(index, value)
    }
  }

  const searchPartnerCEP = async (index, cep) => {
    setLoadingPartnerCEP(prev => ({ ...prev, [index]: true }))
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)

      if (response.ok) {
        const data = await response.json()

        if (data.erro) {
          toast.error('CEP não encontrado')
        } else {
          const updatedPartners = [...formData.partners]
          updatedPartners[index] = {
            ...updatedPartners[index],
            street: data.logradouro || updatedPartners[index].street,
            neighborhood: data.bairro || updatedPartners[index].neighborhood,
            city: data.localidade || updatedPartners[index].city,
            state: data.uf || updatedPartners[index].state,
            complement: data.complemento || updatedPartners[index].complement
          }
          setFormData(prev => ({ ...prev, partners: updatedPartners }))

          toast.success(`Endereço do Sócio ${index + 1} preenchido automaticamente!`)
        }
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error)
      toast.error('Erro ao consultar CEP')
    } finally {
      setLoadingPartnerCEP(prev => ({ ...prev, [index]: false }))
    }
  }

  const handlePartnerChange = (index, field, value) => {
    const updatedPartners = [...formData.partners]
    updatedPartners[index][field] = value

    // Auto-detectar nacionalidade baseado no nome
    if (field === 'name' && value.trim()) {
      const firstName = value.trim().split(' ')[0].toLowerCase()

      // Nomes femininos comuns brasileiros
      const feminineNames = ['maria', 'ana', 'julia', 'gabriela', 'beatriz', 'carolina', 'fernanda', 'amanda', 'leticia', 'camila', 'mariana', 'rafaela', 'larissa', 'jessica', 'bruna', 'patricia', 'paula', 'renata', 'tatiana', 'luciana', 'fabiana', 'adriana', 'juliana', 'daniela', 'isabela', 'gabriella', 'vitoria', 'sofia']

      // Nomes masculinos comuns brasileiros
      const masculineNames = ['joao', 'jose', 'antonio', 'francisco', 'carlos', 'paulo', 'pedro', 'lucas', 'luis', 'marcos', 'rafael', 'gabriel', 'daniel', 'guilherme', 'bruno', 'andre', 'fernando', 'ricardo', 'rodrigo', 'thiago', 'vitor', 'leonardo', 'matheus', 'gustavo', 'felipe']

      let isFeminine = false

      if (feminineNames.includes(firstName)) {
        isFeminine = true
      } else if (masculineNames.includes(firstName)) {
        isFeminine = false
      } else {
        // Fallback: se termina em 'a' provavelmente é feminino
        isFeminine = firstName.endsWith('a') && !firstName.endsWith('ua')
      }

      updatedPartners[index].nationality = isFeminine ? 'Brasileira' : 'Brasileiro'
    }

    setFormData(prev => ({ ...prev, partners: updatedPartners }))
  }

  const handlePartnerDocumentChange = (index, e) => {
    let value = e.target.value.replace(/\D/g, '')
    let formattedValue = value
    if (value.length <= 11) {
      formattedValue = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    } else {
      formattedValue = value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    }
    handlePartnerChange(index, 'document', formattedValue)
  }

  const addPartner = () => {
    setFormData(prev => ({
      ...prev,
      partners: [...prev.partners, {
        name: '',
        document: '',
        rg: '',
        nationality: 'Brasileira',
        email: '',
        participation: '',
        cep: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        cnh_file: null,
        cnh_file_path: '',
        cnh_data: null
      }]
    }))
  }

  const removePartner = (index) => {
    if (formData.partners.length > 1) {
      const updatedPartners = formData.partners.filter((_, i) => i !== index)
      setFormData(prev => ({ ...prev, partners: updatedPartners }))
    }
  }

  const handlePartnerCNHUpload = (index, file) => {
    if (!file) return

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf']
    if (!validTypes.includes(file.type)) {
      toast.error('Apenas imagens (JPEG, PNG, WEBP) ou PDF são permitidos')
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Tamanho máximo: 5MB')
      return
    }

    // Update partner with file
    const updatedPartners = [...formData.partners]
    updatedPartners[index] = {
      ...updatedPartners[index],
      cnh_file: file
    }
    setFormData(prev => ({ ...prev, partners: updatedPartners }))
    toast.success('CNH anexada! Clique em "Ler CNH" para extrair os dados.')
  }

  const handleViewPartnerCNH = async (index) => {
    try {
      const partnerId = index
      const response = await api.get(`/api/clients/${client.id}/partners/${partnerId}/cnh`)

      if (response.data.success) {
        const cnhData = response.data.data
        // Open CNH document in new tab
        const fileUrl = `${import.meta.env.VITE_API_URL}${cnhData.filePath}`
        window.open(fileUrl, '_blank')
        toast.success('CNH aberta em nova aba')
      }
    } catch (error) {
      console.error('Erro ao visualizar CNH:', error)
      toast.error('Erro ao carregar CNH: ' + (error.response?.data?.error || error.message))
    }
  }

  const handlePartnerCNHScan = async (index) => {
    const partner = formData.partners[index]

    if (!partner.cnh_file) {
      toast.error('Por favor, selecione um arquivo primeiro')
      return
    }

    setLoadingPartnerOCR(prev => ({ ...prev, [index]: true }))
    toast.loading('Processando CNH... Isso pode levar até 2 minutos.', { duration: 120000 })

    try {
      // Create FormData with correct field name (same as DriversPage)
      const formDataOCR = new FormData()
      formDataOCR.append('document', partner.cnh_file)

      // Use CORRECT endpoint: /api/ocr/process-cnh
      const response = await api.post('/api/ocr/process-cnh', formDataOCR, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000 // 120 segundos para OCR processar
      })

      if (response.data.success) {
        const cnhData = response.data.data

        console.log('📄 CNH Data recebida do backend:', cnhData)

        // Update partner with OCR data
        const updatedPartners = [...formData.partners]
        updatedPartners[index] = {
          ...updatedPartners[index],
          cnh_data: cnhData,
          name: cnhData.nome || updatedPartners[index].name,
          document: cnhData.cpf || updatedPartners[index].document,
          rg: cnhData.rg || updatedPartners[index].rg
        }
        setFormData(prev => ({ ...prev, partners: updatedPartners }))

        toast.dismiss() // Remove loading toast
        toast.success(`CNH processada! ${response.data.fieldsExtracted} campos extraídos.`)

        // If editing an existing client, save CNH file permanently
        if (client && client.id) {
          try {
            const uploadData = new FormData()
            uploadData.append('cnh', partner.cnh_file)

            const uploadResponse = await api.post(
              `/api/clients/${client.id}/partners/${index}/upload-cnh`,
              uploadData,
              {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              }
            )

            if (uploadResponse.data.success) {
              // Update partner with permanent file path
              updatedPartners[index].cnh_file_path = uploadResponse.data.filePath
              setFormData(prev => ({ ...prev, partners: updatedPartners }))
              toast.success('CNH salva com sucesso!')
            }
          } catch (uploadError) {
            console.error('Erro ao salvar CNH permanentemente:', uploadError)
            toast.warning('CNH lida, mas não foi salva permanentemente. Salve o cliente para armazenar.')
          }
        }
      } else {
        toast.dismiss()
        toast.error('Não foi possível extrair dados do documento')
      }
    } catch (error) {
      console.error('Erro ao processar CNH:', error)
      toast.dismiss() // Remove loading toast
      toast.error(error.response?.data?.error || 'Erro ao processar CNH')
    } finally {
      setLoadingPartnerOCR(prev => ({ ...prev, [index]: false }))
      toast.dismiss() // Remove loading toast
    }
  }

  const validateCPF = (cpf) => {
    const numbers = cpf.replace(/\D/g, '')
    if (numbers.length !== 11 || /^(\d)\1{10}$/.test(numbers)) return false
    
    let sum = 0
    for (let i = 0; i < 9; i++) sum += parseInt(numbers[i]) * (10 - i)
    let digit = (sum * 10) % 11
    if (digit === 10) digit = 0
    if (digit !== parseInt(numbers[9])) return false
    
    sum = 0
    for (let i = 0; i < 10; i++) sum += parseInt(numbers[i]) * (11 - i)
    digit = (sum * 10) % 11
    if (digit === 10) digit = 0
    return digit === parseInt(numbers[10])
  }

  const validateCNPJ = (cnpj) => {
    const numbers = cnpj.replace(/\D/g, '')
    if (numbers.length !== 14 || /^(\d)\1{13}$/.test(numbers)) return false
    
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
    
    let sum = 0
    for (let i = 0; i < 12; i++) sum += parseInt(numbers[i]) * weights1[i]
    let digit = sum % 11
    digit = digit < 2 ? 0 : 11 - digit
    if (digit !== parseInt(numbers[12])) return false
    
    sum = 0
    for (let i = 0; i < 13; i++) sum += parseInt(numbers[i]) * weights2[i]
    digit = sum % 11
    digit = digit < 2 ? 0 : 11 - digit
    return digit === parseInt(numbers[13])
  }

  const validateDocument = (document) => {
    const numbers = document.replace(/\D/g, '')
    if (numbers.length === 11) return validateCPF(document)
    if (numbers.length === 14) return validateCNPJ(document)
    return false
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (!formData.name.trim()) {
        toast.error('Nome é obrigatório')
        return
      }

      if (!formData.document.trim()) {
        toast.error('Documento é obrigatório')
        return
      }

      if (!validateDocument(formData.document)) {
        toast.error('Documento inválido')
        return
      }

      if (formData.email && !formData.email.includes('@')) {
        toast.error('Email inválido')
        return
      }

      const dataToSend = {
        ...formData,
        billing_emails: formData.billing_emails.filter(e => e && e.includes('@')),
        address: {
          cep: formData.cep,
          street: formData.street,
          number: formData.number,
          complement: formData.complement,
          neighborhood: formData.neighborhood,
          city: formData.city,
          state: formData.state
        }
      }

      const response = isEditing
        ? await api.put(`/api/clients/${client.id}`, dataToSend)
        : await api.post('/api/clients', dataToSend)

      const result = response.data
      const savedClientId = isEditing ? client.id : result.client?.id

      // Upload any CNH files that haven't been permanently saved yet
      if (savedClientId) {
        const uploadPromises = formData.partners.map(async (partner, index) => {
          // Only upload if partner has a CNH file but no permanent file path yet
          if (partner.cnh_file && !partner.cnh_file_path) {
            try {
              const uploadData = new FormData()
              uploadData.append('cnh', partner.cnh_file)

              const uploadResponse = await api.post(
                `/api/clients/${savedClientId}/partners/${index}/upload-cnh`,
                uploadData,
                {
                  headers: {
                    'Content-Type': 'multipart/form-data'
                  }
                }
              )

              if (uploadResponse.data.success) {
                console.log(`✅ CNH do sócio ${index} salva: ${uploadResponse.data.filename}`)
              }
            } catch (uploadError) {
              console.error(`❌ Erro ao salvar CNH do sócio ${index}:`, uploadError)
            }
          }
        })

        // Wait for all uploads to complete
        await Promise.all(uploadPromises)
      }

      toast.success(isEditing ? 'Cliente atualizado!' : 'Cliente adicionado!')
      onSuccess && onSuccess()
      onClose()
    } catch (error) {
      console.error('Erro:', error)
      const message = error.response?.data?.error || 'Erro ao conectar com servidor'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <UserIcon className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {isEditing ? `Editar Cliente - ${client.name}` : 'Novo Cliente'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Tab Navigation */}
          <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-6">
            <button
              type="button"
              onClick={() => setActiveTab('dados-gerais')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'dados-gerais'
                  ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Dados Gerais
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('procurador')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'procurador'
                  ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Procurador
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('socios')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'socios'
                  ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Sócios
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('documentos')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'documentos'
                  ? 'text-blue-600 border-b-2 border-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              Documentos
            </button>
          </div>

          {/* Tab Content: Dados Gerais */}
          {activeTab === 'dados-gerais' && (
          <>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase">Dados Principais</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  CPF/CNPJ * {loadingCNPJ && <span className="text-blue-600 text-xs">(buscando...)</span>}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="document"
                    value={formData.document}
                    onChange={handleDocumentChange}
                    onBlur={() => setDocumentTouched(true)}
                    required
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    maxLength="18"
                    className={`w-full px-3 py-2 pr-10 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${getValidationClass(formData.document, documentTouched) || 'border-gray-300 dark:border-gray-600'}`}
                  />
                  {loadingCNPJ && <MagnifyingGlassIcon className="absolute right-3 top-2.5 w-5 h-5 text-blue-600 animate-pulse" />}
                </div>
                {documentTouched && formData.document && (
                  <p className={`text-xs mt-1 ${getValidationMessage(formData.document, documentTouched).isValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {getValidationMessage(formData.document, documentTouched).message}
                  </p>
                )}
                {!documentTouched && <p className="text-xs text-gray-500 mt-1">CNPJ preenche automaticamente</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nome Completo / Razão Social *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Ex: João Silva Santos"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="joao@email.com"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Telefone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="(11) 99999-9999"
                  maxLength="15"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Emails de Cobrança */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase">Emails de Cobranca</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Faturas e lembretes serao enviados para todos estes emails</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, billing_emails: [...prev.billing_emails, ''] }))}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
              >
                + Adicionar Email
              </button>
            </div>
            <div className="space-y-2">
              {formData.billing_emails.map((billingEmail, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="email"
                    value={billingEmail}
                    onChange={(e) => {
                      const updated = [...formData.billing_emails]
                      updated[index] = e.target.value
                      setFormData(prev => ({ ...prev, billing_emails: updated }))
                    }}
                    placeholder={`Email de cobranca ${index + 1}`}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {formData.billing_emails.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const updated = formData.billing_emails.filter((_, i) => i !== index)
                        setFormData(prev => ({ ...prev, billing_emails: updated }))
                      }}
                      className="px-3 py-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                      title="Remover email"
                    >
                      X
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase">Endereco</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  CEP {loadingCEP && <span className="text-blue-600 text-xs">(buscando...)</span>}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="cep"
                    value={formData.cep}
                    onChange={handleCEPChange}
                    placeholder="00000-000"
                    maxLength="9"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {loadingCEP && <MagnifyingGlassIcon className="absolute right-3 top-2.5 w-5 h-5 text-blue-600 animate-pulse" />}
                </div>
                <p className="text-xs text-gray-500 mt-1">Preenche automaticamente</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rua/Logradouro</label>
                <input type="text" name="street" value={formData.street} onChange={handleInputChange} placeholder="Ex: Rua das Flores" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Número</label>
                <input type="text" name="number" value={formData.number} onChange={handleInputChange} placeholder="123" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Complemento</label>
                <input type="text" name="complement" value={formData.complement} onChange={handleInputChange} placeholder="Apt 101" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bairro</label>
                <input type="text" name="neighborhood" value={formData.neighborhood} onChange={handleInputChange} placeholder="Centro" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cidade</label>
                <input type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="São Paulo" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Estado</label>
                <select name="state" value={formData.state} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <option value="">Selecione</option>
                  <option value="AC">AC</option><option value="AL">AL</option><option value="AP">AP</option><option value="AM">AM</option>
                  <option value="BA">BA</option><option value="CE">CE</option><option value="DF">DF</option><option value="ES">ES</option>
                  <option value="GO">GO</option><option value="MA">MA</option><option value="MT">MT</option><option value="MS">MS</option>
                  <option value="MG">MG</option><option value="PA">PA</option><option value="PB">PB</option><option value="PR">PR</option>
                  <option value="PE">PE</option><option value="PI">PI</option><option value="RJ">RJ</option><option value="RN">RN</option>
                  <option value="RS">RS</option><option value="RO">RO</option><option value="RR">RR</option><option value="SC">SC</option>
                  <option value="SP">SP</option><option value="SE">SE</option><option value="TO">TO</option>
                </select>
              </div>
            </div>
          </div>

          {/* Análise de Crédito */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase">Análise de Crédito</h3>

            {isEditing && client?.id ? (
              <>
                <CreditScoreWidget
                  clientId={client.id}
                  clientDocument={formData.document}
                  onAnalysisComplete={(data) => {
                    console.log('Análise concluída:', data);
                  }}
                />
                <CreditHistoryWidget clientId={client.id} />
              </>
            ) : (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <ShieldCheckIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                      Análise de Crédito Disponível Após Cadastro
                    </h4>
                    <p className="text-xs text-yellow-700 dark:text-yellow-300">
                      Após salvar o cliente, você poderá realizar análise de crédito automática através de bureaus de crédito
                      (Serasa, Boa Vista, SPC) ou inserir manualmente o score. A análise ajuda a determinar o nível de risco
                      e as garantias necessárias para locação.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">Funcionalidades automáticas:</h4>
            <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
              <li>• CNPJ: Ao digitar 14 números, busca dados da empresa</li>
              <li>• CEP: Ao digitar 8 números, preenche endereço da empresa</li>
              <li>• CPF/CNPJ: Validação automática do documento</li>
            </ul>
          </div>
        </>
        )}

        {/* Tab Content: Procurador */}
        {activeTab === 'procurador' && (
        <>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg mb-6">
            <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">Procurador / Representante Legal</h4>
            <p className="text-xs text-yellow-700 dark:text-yellow-300">
              Preencha quando uma pessoa com procuração assina contratos em nome da empresa. Os dados serão usados automaticamente ao criar contratos.
            </p>
          </div>

          {!formData.legal_representative ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400 mb-4">Nenhum procurador cadastrado</p>
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  legal_representative: {
                    nome: '', cpf: '', rg: '', orgao_emissor: '', nacionalidade: 'Brasileiro(a)',
                    estado_civil: '', profissao: 'Procurador(a)',
                    instrumento: 'Instrumento Particular de Procuração', data_validade: '',
                    endereco: { cep: '', street: '', number: '', complement: '', neighborhood: '', city: '', state: '' }
                  }
                }))}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                + Cadastrar Procurador
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, legal_representative: null }))}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Remover Procurador
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome Completo *</label>
                  <input
                    type="text"
                    value={formData.legal_representative.nome}
                    onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, nome: e.target.value } }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Nome completo do procurador"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPF *</label>
                  <input
                    type="text"
                    value={formData.legal_representative.cpf}
                    onChange={e => {
                      let v = e.target.value.replace(/\D/g,'').slice(0,11)
                      v = v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/,'$1.$2.$3-$4')
                      setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, cpf: v } }))
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="000.000.000-00"
                    maxLength="14"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">RG</label>
                  <input
                    type="text"
                    value={formData.legal_representative.rg}
                    onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, rg: e.target.value } }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="00.000.000-0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Órgão Emissor</label>
                  <input
                    type="text"
                    value={formData.legal_representative.orgao_emissor}
                    onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, orgao_emissor: e.target.value } }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Ex: SSP/MS"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nacionalidade</label>
                  <input
                    type="text"
                    value={formData.legal_representative.nacionalidade}
                    onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, nacionalidade: e.target.value } }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estado Civil</label>
                  <select
                    value={formData.legal_representative.estado_civil}
                    onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, estado_civil: e.target.value } }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Selecione</option>
                    <option>Solteiro(a)</option>
                    <option>Casado(a)</option>
                    <option>Divorciado(a)</option>
                    <option>Viúvo(a)</option>
                    <option>União Estável</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Instrumento de Procuração</label>
                  <input
                    type="text"
                    value={formData.legal_representative.instrumento}
                    onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, instrumento: e.target.value } }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Ex: Instrumento Particular de Procuração"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data de Validade da Procuração</label>
                  <input
                    type="date"
                    value={formData.legal_representative.data_validade || ''}
                    onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, data_validade: e.target.value } }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase">Endereço do Procurador</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CEP</label>
                    <input
                      type="text"
                      value={formData.legal_representative.endereco?.cep || ''}
                      onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, endereco: { ...prev.legal_representative.endereco, cep: e.target.value } } }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="00000-000"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Logradouro</label>
                    <input
                      type="text"
                      value={formData.legal_representative.endereco?.street || ''}
                      onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, endereco: { ...prev.legal_representative.endereco, street: e.target.value } } }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Número</label>
                    <input
                      type="text"
                      value={formData.legal_representative.endereco?.number || ''}
                      onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, endereco: { ...prev.legal_representative.endereco, number: e.target.value } } }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cidade</label>
                    <input
                      type="text"
                      value={formData.legal_representative.endereco?.city || ''}
                      onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, endereco: { ...prev.legal_representative.endereco, city: e.target.value } } }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Estado</label>
                    <input
                      type="text"
                      value={formData.legal_representative.endereco?.state || ''}
                      onChange={e => setFormData(prev => ({ ...prev, legal_representative: { ...prev.legal_representative, endereco: { ...prev.legal_representative.endereco, state: e.target.value } } }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      maxLength="2"
                      placeholder="MS"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
        )}

        {/* Tab Content: Sócios */}
        {activeTab === 'socios' && (
        <>
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-6">
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">Funcionalidades automáticas para Sócios:</h4>
            <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
              <li>• CEP dos Sócios: Ao digitar 8 números, preenche endereço residencial de cada sócio</li>
              <li>• CPF/CNPJ: Validação automática do documento</li>
              <li>• CNH: Anexe e leia CNH para preencher dados automaticamente (Nome, CPF, RG)</li>
            </ul>
          </div>

          {/* Sócios da Empresa */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Sócios da Empresa</h3>
              <button
                type="button"
                onClick={addPartner}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                + Adicionar Sócio
              </button>
            </div>

            {formData.partners.map((partner, index) => (
              <div key={index} className="mb-4 p-4 border border-gray-300 dark:border-gray-600 rounded-lg relative">
                <button
                  type="button"
                  onClick={() => removePartner(index)}
                  className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                >
                  ✕
                </button>
                <h4 className="text-md font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Sócio {index + 1}
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Nome Completo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nome Completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={partner.name}
                      onChange={(e) => handlePartnerChange(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  {/* CPF/CNPJ */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      CPF/CNPJ <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={partner.document}
                      onChange={(e) => handlePartnerDocumentChange(index, e)}
                      onBlur={() => setPartnerDocumentsTouched(prev => ({ ...prev, [index]: true }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${partnerDocumentsTouched[index] && partner.document ? (validateDocument(partner.document) ? 'border-green-500' : 'border-red-500') : 'border-gray-300 dark:border-gray-600'}`}
                      placeholder="000.000.000-00"
                      maxLength="18"
                      required
                    />
                    {partnerDocumentsTouched[index] && partner.document && (
                      <p className={`text-xs mt-1 ${validateDocument(partner.document) ? 'text-green-600' : 'text-red-600'}`}>
                        {validateDocument(partner.document) ? 'Documento válido ✓' : 'Documento inválido'}
                      </p>
                    )}
                  </div>

                  {/* Participação */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Participação (%) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={partner.participation}
                      onChange={(e) => handlePartnerChange(index, 'participation', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      required
                    />
                  </div>

                  {/* RG */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      RG
                    </label>
                    <input
                      type="text"
                      value={partner.rg || ''}
                      onChange={(e) => handlePartnerChange(index, 'rg', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="00.000.000-0"
                    />
                  </div>

                  {/* Nacionalidade */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nacionalidade
                    </label>
                    <input
                      type="text"
                      value={partner.nationality || partner.nacionalidade || ''}
                      onChange={(e) => handlePartnerChange(index, 'nationality', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Brasileira"
                    />
                  </div>

                  {/* Email do Sócio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email do Sócio
                    </label>
                    <input
                      type="email"
                      value={partner.email}
                      onChange={(e) => handlePartnerChange(index, 'email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="email@exemplo.com"
                    />
                  </div>

                  {/* É representante legal (checkbox) */}
                  <div className="col-span-3 flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id={`is_legal_rep_${index}`}
                      checked={partner.is_legal_representative || false}
                      onChange={(e) => handlePartnerChange(index, 'is_legal_representative', e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label
                      htmlFor={`is_legal_rep_${index}`}
                      className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      É representante legal da empresa
                    </label>
                  </div>

                  {/* CNH Upload and Scan */}
                  <div className="col-span-3 mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                    <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3 uppercase">
                      Documento CNH
                    </h4>
                    <div className="flex gap-2">
                      <label className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer text-center text-sm font-medium transition-colors">
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          className="hidden"
                          onChange={(e) => handlePartnerCNHUpload(index, e.target.files[0])}
                        />
                        📎 Anexar CNH
                      </label>
                      <button
                        type="button"
                        onClick={() => handlePartnerCNHScan(index)}
                        disabled={!partner.cnh_file || loadingPartnerOCR[index]}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
                      >
                        {loadingPartnerOCR[index] ? '🔄 Processando...' : '🔍 Ler CNH'}
                      </button>
                    </div>
                    {partner.cnh_file && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                        📄 Arquivo: {partner.cnh_file.name}
                      </p>
                    )}
                  </div>

                  {/* Saved CNH Document Viewer */}
                  {partner.cnh_file_path && (
                    <div className="col-span-3 mt-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3 flex items-center gap-2">
                        💾 CNH Salva no Sistema
                      </h4>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded font-medium">
                            Documento Armazenado
                          </span>
                          <span>
                            {partner.cnh_file_path.split('/').pop()}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewPartnerCNH(index)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium transition-colors flex items-center gap-1"
                          >
                            👁️ Visualizar CNH
                          </button>
                          <label className="px-3 py-1.5 bg-orange-600 text-white rounded hover:bg-orange-700 cursor-pointer text-xs font-medium transition-colors flex items-center gap-1">
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              className="hidden"
                              onChange={(e) => handlePartnerCNHUpload(index, e.target.files[0])}
                            />
                            🔄 Substituir CNH
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Endereço Residencial */}
                  <div className="col-span-3 mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                    <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3 uppercase">
                      Endereço Residencial do Sócio
                    </h4>
                  </div>

                  {/* CEP */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      CEP
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={partner.cep}
                        onChange={(e) => handlePartnerCEPChange(index, e)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="00000-000"
                        maxLength="9"
                      />
                      {loadingPartnerCEP[index] && (
                        <div className="absolute right-3 top-2.5">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rua */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Rua
                    </label>
                    <input
                      type="text"
                      value={partner.street}
                      onChange={(e) => handlePartnerChange(index, 'street', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Número */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Número
                    </label>
                    <input
                      type="text"
                      value={partner.number}
                      onChange={(e) => handlePartnerChange(index, 'number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Complemento */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Complemento
                    </label>
                    <input
                      type="text"
                      value={partner.complement}
                      onChange={(e) => handlePartnerChange(index, 'complement', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Bairro */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Bairro
                    </label>
                    <input
                      type="text"
                      value={partner.neighborhood}
                      onChange={(e) => handlePartnerChange(index, 'neighborhood', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Cidade */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Cidade
                    </label>
                    <input
                      type="text"
                      value={partner.city}
                      onChange={(e) => handlePartnerChange(index, 'city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Estado */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Estado
                    </label>
                    <input
                      type="text"
                      value={partner.state}
                      onChange={(e) => handlePartnerChange(index, 'state', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      maxLength="2"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
        )}

        {/* Tab Content: Documentos */}
        {activeTab === 'documentos' && (
          <ClientDocumentsTab clientId={client?.id} />
        )}

        {/* Submit Buttons - Outside all tabs */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {loading ? (isEditing ? 'Atualizando...' : 'Salvando...') : (isEditing ? 'Salvar Alterações' : 'Salvar Cliente')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ClientForm